import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// remark-gfm adds tables, strikethrough, task lists, and bare-URL autolinking.
// Without it those render as literal syntax.
const components = {
  // Outbound links in authored content should not carry referrer or opener.
  a: ({ href, children }: { href?: string; children?: ReactNode }) => {
    const isExternal = Boolean(href && /^https?:\/\//i.test(href));
    return (
      <a href={href} {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
        {children}
      </a>
    );
  },
};

const markdownSx: SxProps<Theme> = {
  '& h1': { typography: 'h4', fontWeight: 700, mt: 3, mb: 1 },
  '& h2': { typography: 'h5', fontWeight: 600, mt: 2.5, mb: 1 },
  '& h3': { typography: 'h6', fontWeight: 600, mt: 2, mb: 0.5 },
  '& p': { typography: 'body1', mb: 1.5 },
  '& ul, & ol': { pl: 3, mb: 1.5 },
  '& li': { typography: 'body1', mb: 0.5 },
  '& code': { fontFamily: 'monospace', backgroundColor: '#f5f5f5', px: 0.5, borderRadius: 0.5 },
  '& pre': { backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, overflowX: 'auto', mb: 2 },
  '& blockquote': { borderLeft: '4px solid #e0e0e0', pl: 2, ml: 0, color: 'text.secondary' },
  '& a': { color: 'primary.main' },
  '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
  '& hr': { border: 'none', borderTop: '1px solid #e0e0e0', my: 3 },
  // GFM extensions
  '& table': { borderCollapse: 'collapse', width: '100%', mb: 2, display: 'block', overflowX: 'auto' },
  '& th, & td': { border: '1px solid #e0e0e0', px: 1.5, py: 1, textAlign: 'left', verticalAlign: 'top' },
  '& th': { backgroundColor: '#f5f5f5', fontWeight: 600 },
  '& del': { color: 'text.secondary' },
  '& li:has(> input[type="checkbox"])': { listStyle: 'none', ml: -3 },
  '& li > input[type="checkbox"]': { mr: 1 },
};

interface MarkdownViewProps {
  children: string;
  sx?: SxProps<Theme>;
}

/** Renders authored markdown (tip and blog bodies) with GFM support. */
export function MarkdownView({ children, sx }: MarkdownViewProps) {
  return (
    <Box sx={sx ? [markdownSx, sx] as SxProps<Theme> : markdownSx}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </Box>
  );
}
